import { createSelector } from 'reselect';
import filter from 'lodash/filter';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import map from 'lodash/map';
import get from 'lodash/get';
import some from 'lodash/some';
import has from 'lodash/has';
import memoize from 'lodash/memoize';
import {
    selectProductDetailsBySource,
    selectInventoryBySource,
    selectProductOfferingBySource,
} from './PdpNameSpaceSelector';
import { initProductOptions, isSelectedOptionsEqual } from './ProductOptionsSelector';
import { getPriceFormatWithCommas } from '../helpers/Pricing/Pricing';
import { selectFeatureFlags } from './ContextSelector';
import ProductConstants from '../common/ProductConstants';

// Currently Selected Delivery Option Name
const getDeliveryOptionsName = (state) => state.deliveryOptionsName || {};

/* istanbul ignore next */
const getLotPriceRanges = (priceRange, priceType) => {
    const lotPriceRange = priceRange.map(
        ({ amounts = [] }) =>
            filter(amounts, ({ type = '' }) => type.toUpperCase() === priceType)[0]
    );
    return lotPriceRange;
};

/* istanbul ignore next */
const getPriceRangeForGiftCards = (lotPrice = []) => {
    const [priceRange = {}] = lotPrice;
    const originalLotPrices = getLotPriceRanges(lotPrice, 'ORIGINAL');
    const minOriginalPriceObj = minBy(originalLotPrices, 'min') || {};
    const { max: maxOriginalPrice } = maxBy(originalLotPrices, 'max') || {};
    const defaultLotPrices = getLotPriceRanges(lotPrice, 'DEFAULT');
    const minDefaultPriceObj = minBy(defaultLotPrices, 'min') || {};
    const { max: maxDefaultPrice } = maxBy(defaultLotPrices, 'max') || {};
    priceRange.amounts = [];
    priceRange.amounts.push({ ...minOriginalPriceObj, max: maxOriginalPrice });
    priceRange.amounts.push({ ...minDefaultPriceObj, max: maxDefaultPrice });
    return priceRange;
};

const getProductPricingFallBack = (state) => state.ProductPricingFallBack;

const getLotPrice = (sourceMiniPDP) =>
    createSelector(
        [
            selectProductDetailsBySource(sourceMiniPDP),
            selectInventoryBySource(sourceMiniPDP),
            getProductPricingFallBack,
            initProductOptions(sourceMiniPDP),
        ],
        (productDetails, inventory, ProductPricingFallBack, productOptions) => {
            let lotPrice = null;
            const isGiftcard = productDetails.type === 'GIFTCARD';
            let selectedLot = filter(productDetails.lots, { isDefault: true });
            const { optionsSelected } = productOptions;
            /* istanbul ignore next */
            if (inventory && inventory[0]) {
                if (inventory[0].lotPrice) {
                    lotPrice = inventory[0].lotPrice.data;
                } else if (
                    ProductPricingFallBack &&
                    Object.keys(ProductPricingFallBack).length > 0
                ) {
                    lotPrice = ProductPricingFallBack.data;
                }
                /* istanbul ignore next */
                if (optionsSelected.length) {
                    const filterValues = map(
                        optionsSelected,
                        ({ option: value, dimensionName: name }) => ({ name, value })
                    );
                    selectedLot = filter(productDetails.lots, ({ items }) =>
                        some(items, isSelectedOptionsEqual(filterValues))
                    );
                }
                const selectedLotPrice = selectedLot[0]
                    ? find(lotPrice, {
                          id: selectedLot[0].id,
                      })
                    : null;
                /* istanbul ignore next */
                if (selectedLotPrice) {
                    if (!optionsSelected.length && isGiftcard) {
                        lotPrice = getPriceRangeForGiftCards(lotPrice);
                    } else {
                        lotPrice = selectedLotPrice;
                    }
                }
                if (lotPrice) {
                    lotPrice.hybridIndicator = productDetails
                        ? productDetails.hybridIndicator
                        : false;
                }
            } else {
                lotPrice = null;
            }
            return lotPrice;
        }
    );

// const getProductOfferingPrice = state => state.productOffering || {};

const getAllLotPrice = (sourceMiniPDP) =>
    createSelector(
        [selectInventoryBySource(sourceMiniPDP), getProductPricingFallBack],
        (inventory, ProductPricingFallBack) => {
            let allLotPrice = [];
            if (inventory && inventory[0]) {
                if (inventory[0].lotPrice) {
                    allLotPrice = inventory[0].lotPrice.data;
                } else if (
                    ProductPricingFallBack &&
                    Object.keys(ProductPricingFallBack).length > 0
                ) {
                    allLotPrice = ProductPricingFallBack.data;
                }
            }
            return allLotPrice;
        }
    );

export const getPriceFormat = (min, max, isCurrency) => {
    let priceMatch;
    const dollarSymbol = '$';
    const percentageOff = '% off';
    if (isCurrency) {
        priceMatch = getPriceFormatWithCommas(
            min === max ? `${dollarSymbol}${max}` : `${dollarSymbol}${min} - ${dollarSymbol}${max}`
        );
    } else {
        priceMatch = min === max ? `${min}${percentageOff}` : `${min} - ${max}${percentageOff}`;
    }
    return priceMatch;
};

const getPrice = (priceData, inventoryPrice) => {
    let prices = priceData;
    /* istanbul ignore next */
    if (priceData[0]) {
        prices = priceData[0];
    }
    const originalPrice = filter(
        prices.amounts,
        (amount) => amount.type.toUpperCase() === 'ORIGINAL'
    )[0];
    const defaultPrice = filter(
        prices.amounts,
        (amount) => amount.type.toUpperCase() !== 'ORIGINAL'
    )[0];
    let defaults = null;
    let type = null;
    let percentOff = null;
    let original = '';
    let originalType = '';
    let dashed = '';
    /* istanbul ignore next */
    let originalPriceMin = originalPrice ? originalPrice.min : 0;
    /* istanbul ignore next */
    let originalPriceMax = originalPrice ? originalPrice.max : 0;
    let mapPrice = '';
    let promotions;
    let marketingLabel;
    const fpac = prices.couponInfo;
    const isCouponAmount = fpac && fpac.length && fpac[0].amount?.min === fpac[0].amount?.max;
    /* istanbul ignore next */
    if (inventoryPrice) {
        promotions = inventoryPrice.promotions;
        let clearenceType = false;
        /* istanbul ignore next */
        if (inventoryPrice.hybridIndicator && defaultPrice !== undefined) {
            clearenceType =
                (defaultPrice &&
                    defaultPrice.type &&
                    defaultPrice.type.toLowerCase() === 'clearance') ||
                false;
        }
        /* istanbul ignore next */
        if (clearenceType) {
            marketingLabel = prices.marketingLabel ? prices.marketingLabel : '';
        } else {
            marketingLabel = inventoryPrice.marketingLabel ? inventoryPrice.marketingLabel : '';
        }
    } else {
        promotions = prices.promotions;
        marketingLabel = prices.marketingLabel ? prices.marketingLabel : '';
    }
    /* istanbul ignore next */
    const surcharges =
        prices.surcharges && prices.surcharges[0].amount
            ? prices.surcharges[0].amount.toFixed(2)
            : '';
    if (!prices.manufacturerAdvertised && defaultPrice !== undefined) {
        /* istanbul ignore next */
        let defaultPriceMin = defaultPrice ? defaultPrice.min : 0;
        /* istanbul ignore next */
        let defaultPriceMax = defaultPrice ? defaultPrice.max : 0;
        defaultPriceMin = !Number.isInteger(defaultPriceMin)
            ? defaultPriceMin.toFixed(2)
            : defaultPriceMin;
        defaultPriceMax = !Number.isInteger(defaultPriceMax)
            ? defaultPriceMax.toFixed(2)
            : defaultPriceMax;
        originalPriceMin = !Number.isInteger(originalPriceMin)
            ? originalPriceMin.toFixed(2)
            : originalPriceMin;
        originalPriceMax = !Number.isInteger(originalPriceMax)
            ? originalPriceMax.toFixed(2)
            : originalPriceMax;

        defaults = getPriceFormat(defaultPriceMin, defaultPriceMax, true);
        type = defaultPrice.type.toUpperCase() !== 'DEFAULT' ? defaultPrice.type.toLowerCase() : '';
        percentOff =
            defaultPrice.minPercentOff > 0
                ? getPriceFormat(defaultPrice.minPercentOff, defaultPrice.maxPercentOff, false)
                : '';
        /* istanbul ignore next */
        if (originalPrice !== undefined) {
            const originalPriceFormat = getPriceFormat(originalPriceMin, originalPriceMax, true);
            if (defaults !== originalPriceFormat) {
                original = originalPriceFormat;
                originalType = 'was';
                dashed = ' | ';
            }
        }
    } else {
        if (!Number.isInteger(originalPriceMin) || !Number.isInteger(originalPriceMax)) {
            originalPriceMin = originalPriceMin.toFixed(2);
            originalPriceMax = originalPriceMax.toFixed(2);
        }
        defaults = getPriceFormat(originalPriceMin, originalPriceMax, true);
        if (prices.manufacturerAdvertised) {
            mapPrice = 'View Price in Cart';
            originalType = 'was';
            original = defaults;
        }
    }

    const price = {
        defaults,
        original,
        originalType,
        percentOff,
        type,
        marketingLabel,
        promotions,
        dashed,
        mapPrice,
        surcharges,
        fpac,
        isCouponAmount,
    };
    return price;
};

const getOfferPricing = (offerPrice, lotPrice, bopisIncentiveEnabled) => {
    let priceData = '';
    const hasCoupon =
        offerPrice.pricing &&
        (offerPrice.pricing.couponInfo || offerPrice.pricing.eligibleCouponInfo);
    /* istanbul ignore next */
    if (offerPrice.calculatePrices) {
        priceData = getPrice({
            amounts: offerPrice.calculatePrices,
        });
        /* istanbul ignore next */
    } else if (offerPrice.pricing) {
        if (
            bopisIncentiveEnabled &&
            offerPrice.pricing &&
            offerPrice.pricing.bopis &&
            lotPrice &&
            lotPrice.bopis
        ) {
            // when coupon applicable price section should display price with bopis discount.
            // when coupon not applicable price section should display price without bopis discount since bopis discount will be displayed in a different section.
            priceData = hasCoupon
                ? getPrice(offerPrice.pricing.bopis, lotPrice.bopis)
                : getPrice(offerPrice.pricing, lotPrice);
            priceData = { ...priceData, hasBopisPrice: true };
        } else {
            // if bopis discount not enabled or not applicable
            priceData = getPrice(offerPrice.pricing, lotPrice);
        }
    }
    return priceData;
};

// returns atleast if one lot eligible as well as selected lot eligible for pickup disount.
export const bopisIncentiveProductEligibilitySelector = memoize((sourceMiniPDP = false) =>
    createSelector(
        [
            getLotPrice(sourceMiniPDP),
            selectProductOfferingBySource(sourceMiniPDP),
            selectInventoryBySource(sourceMiniPDP),
            selectFeatureFlags,
        ],
        (selectedLotPrice, offeringPrice, inventory, featureFlags) => {
            let returnValue = {};
            const bopisIncentiveEnabled = featureFlags.enableBopisIncentive;
            if (bopisIncentiveEnabled) {
                const lotPrices = get(inventory, '[0].lotPrice', {});
                const lotPricesData = get(lotPrices, 'data', []);
                if (lotPricesData) {
                    returnValue = {
                        ...returnValue,
                        atleastOneLotEligible: some(lotPricesData, (lot) => has(lot, 'bopis')),
                    };
                }
                if (Object.keys(offeringPrice).length > 0) {
                    returnValue = {
                        ...returnValue,
                        currentLotEligible: !isEmpty(get(offeringPrice, 'pricing.bopis', {})),
                    };
                } else {
                    returnValue = {
                        ...returnValue,
                        currentLotEligible: !isEmpty(get(selectedLotPrice, 'bopis', {})),
                    };
                }
            }
            return returnValue;
        }
    )
);

export const bopisPromotionMessageSelector = memoize((sourceMiniPDP = false) =>
    createSelector(
        [
            getLotPrice(sourceMiniPDP),
            selectProductOfferingBySource(sourceMiniPDP),
            selectFeatureFlags,
        ],
        (lotPrice, offeringPrice, featureFlags) => {
            let returnValue = false;
            // Since this is Lot Level delivery option not required.
            const bopisIncentiveEnabled = featureFlags.enableBopisIncentive;
            if (Object.keys(offeringPrice).length > 0) {
                returnValue =
                    bopisIncentiveEnabled &&
                    offeringPrice.pricing &&
                    offeringPrice.pricing.bopis &&
                    offeringPrice.pricing.bopis.promotions;
            } else {
                returnValue =
                    bopisIncentiveEnabled &&
                    lotPrice &&
                    lotPrice.bopis &&
                    lotPrice.bopis.promotions;
            }
            return returnValue;
        }
    )
);
export const productPricingSelectorForAllLot = memoize((sourceMiniPDP = false) =>
    createSelector(
        [getAllLotPrice(sourceMiniPDP), selectFeatureFlags, getDeliveryOptionsName],
        (allLotPrice, featureFlags, deliveryOption) => {
            const allPriceData = [];
            if (allLotPrice.length !== 0) {
                allLotPrice.map((lotPrice) => {
                    let priceData;
                    const bopisIncentiveEnabled =
                        featureFlags.enableBopisIncentive &&
                        deliveryOption.selectedOption === ProductConstants.BOPIS_SELECTED_OPTION;
                    const hasCoupon =
                        lotPrice && (lotPrice.couponInfo || lotPrice.eligibleCouponInfo);
                    if (bopisIncentiveEnabled && lotPrice.bopis) {
                        // when coupon applicable price section should display price with bopis discount.
                        // when coupon not applicable price section should display price without bopis discount since bopis discount will be displayed in a different section.
                        priceData = hasCoupon ? getPrice(lotPrice.bopis) : getPrice(lotPrice);
                    } else {
                        // if bopis discount not enabled or not applicable
                        priceData = getPrice(lotPrice);
                        priceData = lotPrice.bopis
                            ? { ...priceData, hasBopisPrice: true, lotId: lotPrice.id }
                            : { ...priceData, lotId: lotPrice.id };
                    }
                    allPriceData.push(priceData);
                });
            }
            return allPriceData;
        }
    )
);

const productPricingSelector = memoize((sourceMiniPDP = false) =>
    createSelector(
        [
            getLotPrice(sourceMiniPDP),
            selectProductOfferingBySource(sourceMiniPDP),
            selectFeatureFlags,
            getDeliveryOptionsName,
        ],
        (lotPrice, offeringPrice, featureFlags, deliveryOption) => {
            let priceData;
            const bopisIncentiveEnabled =
                featureFlags.enableBopisIncentive &&
                deliveryOption.selectedOption === ProductConstants.BOPIS_SELECTED_OPTION;

            if (Object.keys(offeringPrice).length > 0) {
                priceData = getOfferPricing(offeringPrice, lotPrice, bopisIncentiveEnabled);
                priceData = { ...priceData, offeringCall: true };
            } else if (lotPrice) {
                const hasCoupon = lotPrice && (lotPrice.couponInfo || lotPrice.eligibleCouponInfo);
                if (bopisIncentiveEnabled && lotPrice.bopis) {
                    // when coupon applicable price section should display price with bopis discount.
                    // when coupon not applicable price section should display price without bopis discount since bopis discount will be displayed in a different section.
                    priceData = hasCoupon ? getPrice(lotPrice.bopis) : getPrice(lotPrice);
                } else {
                    // if bopis discount not enabled or not applicable
                    priceData = getPrice(lotPrice);
                    priceData = lotPrice.bopis ? { ...priceData, hasBopisPrice: true } : priceData;
                }
            } else {
                priceData = '';
            }
            return priceData;
        }
    )
);

export default productPricingSelector;

// returns price when there is no FPAC but has bopis discount which will be displayed in a seperate section.
export const pickupDiscountSelector = memoize((sourceMiniPDP = false) =>
    createSelector(
        [
            getLotPrice(sourceMiniPDP),
            selectProductOfferingBySource(sourceMiniPDP),
            selectFeatureFlags,
            getDeliveryOptionsName,
        ],
        (lotPrice, offeringPrice, featureFlags, deliveryOption) => {
            let priceData = '';
            const bopisIncentiveEnabled =
                featureFlags.enableBopisIncentive &&
                deliveryOption.selectedOption === ProductConstants.BOPIS_SELECTED_OPTION;
            if (
                Object.keys(offeringPrice).length > 0 &&
                bopisIncentiveEnabled &&
                offeringPrice.pricing &&
                offeringPrice.pricing.bopis &&
                lotPrice &&
                lotPrice.bopis
            ) {
                const hasCoupon =
                    offeringPrice.pricing &&
                    (offeringPrice.pricing.couponInfo || offeringPrice.pricing.eligibleCouponInfo);
                // return price data only when no coupon.
                /* istanbul ignore else */
                if (!hasCoupon) {
                    priceData = getPrice(offeringPrice.pricing.bopis, lotPrice.bopis);
                    priceData = { ...priceData, offeringCall: true };
                }
            }
            return priceData;
        }
    )
);

export const productLotSelector = memoize((sourceMiniPDP = false) =>
    createSelector([getLotPrice(sourceMiniPDP)], (lotPrice) => lotPrice || {})
);
export const selectClearance = memoize((sourceMiniPDP = false) =>
    createSelector(
        [productPricingSelector(sourceMiniPDP)],
        (productPricing = { type: '' }) =>
            !!productPricing.type &&
            productPricing.type.toLowerCase().includes(ProductConstants.CLEARANCE)
    )
);
export const selectCloseout = memoize((sourceMiniPDP = false) =>
    createSelector(
        [productPricingSelector(sourceMiniPDP)],
        (productPricing = { marketingLabel: '' }) =>
            typeof productPricing.marketingLabel === 'string' &&
            productPricing.marketingLabel.toLowerCase().includes(ProductConstants.CLOSEOUT)
    )
);

const getselectedOptions = memoize((sourceMiniPDP = false) =>
    createSelector(
        [initProductOptions(sourceMiniPDP)],
        (productOptions) => productOptions.hybridPrice
    )
);
export const selectSaleandClearence = memoize((sourceMiniPDP = false) =>
    createSelector(
        [
            selectProductDetailsBySource(sourceMiniPDP),
            selectInventoryBySource(sourceMiniPDP),
            getselectedOptions(sourceMiniPDP),
            selectProductOfferingBySource(sourceMiniPDP),
            getLotPrice(sourceMiniPDP),
        ],
        (productDetails, inventory, hybridPriceOptions, offeringPrice, lotPrice) => {
            let clearanceValue;
            let saleValue;
            let originalValue;
            let type;
            let { manufacturerAdvertised = false } = lotPrice || {};
            /* istanbul ignore next */
            if (productDetails.hybridIndicator && inventory && inventory[0]) {
                let allClearenceMin = null;
                let allSaleMax = null;
                let allOrigianlMax = null;
                let allClearenceMax = null;
                let allOrigianlMin = null;
                let allSaleMin = null;
                let itemClearancePriceData = [];
                let itemSalePriceData = [];
                let itemOriginalPriceData = [];
                /* istanbul ignore next */
                if (offeringPrice.pricing) {
                    manufacturerAdvertised = offeringPrice.pricing.manufacturerAdvertised;
                    if (offeringPrice.pricing.amounts) {
                        itemClearancePriceData = filter(
                            offeringPrice.pricing.amounts,
                            (amount) => amount.type.toUpperCase() === 'CLEARANCE'
                        );
                        itemSalePriceData = filter(
                            offeringPrice.pricing.amounts,
                            (amount) => amount.type.toUpperCase() === 'SALE'
                        );
                        itemOriginalPriceData =
                            offeringPrice.pricing.amounts.length === 1
                                ? filter(
                                      offeringPrice.pricing.amounts,
                                      (amount) => amount.type.toUpperCase() === 'ORIGINAL'
                                  )
                                : [];
                    }
                }

                /* istanbul ignore next */
                if (hybridPriceOptions) {
                    allClearenceMin = !isEmpty(itemClearancePriceData)
                        ? minBy(itemClearancePriceData, 'min')
                        : minBy(hybridPriceOptions.allClearence, 'min');
                    allClearenceMax = !isEmpty(itemClearancePriceData)
                        ? maxBy(itemClearancePriceData, 'max')
                        : maxBy(hybridPriceOptions.allClearence, 'max');
                    allSaleMin = !isEmpty(itemSalePriceData)
                        ? minBy(itemSalePriceData, 'min')
                        : minBy(hybridPriceOptions.allSale, 'min');
                    allSaleMax = !isEmpty(itemSalePriceData)
                        ? maxBy(itemSalePriceData, 'max')
                        : maxBy(hybridPriceOptions.allSale, 'max');
                    allOrigianlMin = !isEmpty(itemOriginalPriceData)
                        ? minBy(itemOriginalPriceData, 'min')
                        : minBy(hybridPriceOptions.allOriginal, 'min');
                    allOrigianlMax = !isEmpty(itemOriginalPriceData)
                        ? maxBy(itemOriginalPriceData, 'max')
                        : maxBy(hybridPriceOptions.allOriginal, 'max');
                }

                clearanceValue = allClearenceMin
                    ? getPriceFormat(
                          allClearenceMin.min.toFixed(2),
                          allClearenceMax.max.toFixed(2),
                          true
                      )
                    : null;
                saleValue = allSaleMax
                    ? getPriceFormat(allSaleMin.min.toFixed(2), allSaleMax.max.toFixed(2), true)
                    : null;
                originalValue = allOrigianlMax
                    ? getPriceFormat(
                          allOrigianlMin.min.toFixed(2),
                          allOrigianlMax.max.toFixed(2),
                          true
                      )
                    : null;
            }
            return {
                clearanceValue,
                saleValue,
                type,
                originalValue,
                manufacturerAdvertised,
            };
        }
    )
);
